import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import NoAccess from "./NoAccess";
import FaqSection from "./FaqSection";
import { AccessError, CreateDashboardJob, GetDashboardAvailableDates, GetDashboardJob, GetSolutionMappings } from "../services/ApiService";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  ClearOutlined,
  DollarOutlined,
  EyeOutlined,
  LoadingOutlined,
  ReloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const POLL_INTERVAL_MS = 6000;
const POLL_TIMEOUT_MS = 900 * 1000; // 15 minutes — matches worker Lambda's own timeout

const COLORS = {
  primary: "#1677ff",
  success: "#52c41a",
  warning: "#faad14",
  purple: "#722ed1",
  cyan: "#13c2c2",
  orange: "#fa8c16",
};
const PIE_COLORS = [COLORS.primary, COLORS.orange, COLORS.success, COLORS.purple, COLORS.cyan, COLORS.warning];

// ─── formatters ───────────────────────────────────────────────────────────────
function fmtNum(n) {
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return n?.toLocaleString() ?? "0";
}

function fmtUsd(n) {
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function uniqueOptions(data, field) {
  return [...new Set(data.map((d) => d[field]).filter(Boolean))].sort().map((v) => ({ label: v, value: v }));
}

function upId(id) {
  return id ? id.toUpperCase() : id;
}

function aggregateSolutionType(apps) {
  const buckets = {};
  for (const app of apps) {
    const st = app.solution_type;
    if (!buckets[st]) buckets[st] = { solution_type: st, partner_cost_usd: 0, impressions: 0 };
    buckets[st].partner_cost_usd = +(buckets[st].partner_cost_usd + app.partner_cost_usd).toFixed(2);
    buckets[st].impressions += app.impressions;
  }
  return Object.values(buckets);
}

function buildSummary(apps) {
  const total_impressions = apps.reduce((s, a) => s + a.impressions, 0);
  const total_clicks = apps.reduce((s, a) => s + a.clicks, 0);
  return {
    total_impressions,
    total_clicks,
    total_conversions: apps.reduce((s, a) => s + a.conversions, 0),
    total_spend_usd: +apps.reduce((s, a) => s + a.partner_cost_usd, 0).toFixed(2),
    total_applications: apps.length,
  };
}

// ─── chart helpers ────────────────────────────────────────────────────────────

function AppBarChartTooltip({ active, payload, tooltipFormatter }) {
  if (!active || !payload?.length) return null;
  const record = payload[0].payload;
  const [formattedValue, metricName] = tooltipFormatter(payload[0].value);
  return (
    <div style={{ background: "#fff", border: "1px solid #d9d9d9", borderRadius: 6, padding: "8px 12px", fontSize: 13, maxWidth: 280 }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{upId(record.application_id)}</div>
      {record.jira_epic_name && (
        <div style={{ color: "#666", fontSize: 12, marginBottom: 6 }}>{record.jira_epic_name}</div>
      )}
      <div>{metricName}: <strong>{formattedValue}</strong></div>
    </div>
  );
}

function AppBarChart({ title, data, dataKey, tickFormatter, tooltipFormatter, colorKey }) {
  const isFiltered = data.length <= 10; // already the full filtered set, no slice needed
  const chartData = [...data]
    .sort((a, b) => b[dataKey] - a[dataKey])
    .slice(0, isFiltered ? undefined : 10);

  return (
    <Card title={title} bordered={false}>
      <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 28)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 30 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={tickFormatter} />
          <YAxis type="category" dataKey="application_id" width={110} tick={{ fontSize: 12 }} tickFormatter={upId} />
          <ReTooltip content={<AppBarChartTooltip tooltipFormatter={tooltipFormatter} />} />
          <Bar dataKey={colorKey || dataKey} radius={[0, 4, 4, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.application_id} fill={entry.solution_type === "Solution Accelerator" ? COLORS.orange : COLORS.primary} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div style={{ marginTop: 8, fontSize: 12 }}>
        <span style={{ marginRight: 16 }}>
          <span style={{ background: COLORS.primary, width: 12, height: 12, display: "inline-block", marginRight: 4 }} />
          Standalone Solution
        </span>
        <span>
          <span style={{ background: COLORS.orange, width: 12, height: 12, display: "inline-block", marginRight: 4 }} />
          Solution Accelerator
        </span>
      </div>
    </Card>
  );
}

function SolutionTypePie({ data, dataKey, title }) {
  const fmt = dataKey === "partner_cost_usd" ? fmtUsd : fmtNum;
  const label = dataKey === "partner_cost_usd" ? "Spend" : "Impressions";
  // Only render inline label for slices >= 5% to avoid congestion
  const renderLabel = ({ solution_type, percent }) =>
    percent >= 0.05 ? `${solution_type}: ${(percent * 100).toFixed(1)}%` : "";

  return (
    <Card title={title} bordered={false}>
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey="solution_type"
            cx="50%"
            cy="45%"
            outerRadius={100}
            label={renderLabel}
            labelLine={({ percent }) => percent >= 0.05}
          >
            {data.map((entry, index) => (
              <Cell key={entry.solution_type} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <ReTooltip formatter={(v) => [fmt(v), label]} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );
}

function hint(label, tip) {
  return (
    <Tooltip title={tip}>
      <span style={{ borderBottom: "1px dashed #aaa", cursor: "help" }}>{label}</span>
    </Tooltip>
  );
}

function KpiCards({ summary, ytdKpis }) {
  const periodCards = [
    {
      title: "Total Impressions",
      hint: "Impressions delivered on solution-measured entities in the selected period.",
      value: fmtNum(summary.total_impressions), icon: <EyeOutlined />, color: COLORS.primary,
    },
    {
      title: "Total Spend",
      hint: "Partner cost in USD on solution-measured entities in the selected period",
      value: fmtUsd(summary.total_spend_usd), icon: <DollarOutlined />, color: COLORS.purple,
    },
    {
      title: "Solutions",
      hint: "Number of solutions that appear in the selected period's report.",
      value: summary.total_applications, icon: <TeamOutlined />, color: COLORS.orange,
    },
  ];
  const ytdCards = ytdKpis ? [
    {
      title: "YTD Solutions Live",
      hint: "Solutions (new and existing) with measured entities in this year's report",
      value: ytdKpis.ytd_solutions_live, icon: <TeamOutlined />, color: COLORS.orange,
    },
    // Hidden for now — remove `hidden` to show it again.
    { title: "YTD Brands Live", hidden: true, value: ytdKpis.ytd_brands_live, icon: <TeamOutlined />, color: COLORS.cyan },
    {
      title: "YTD Advertisers",
      hint: "Distinct advertisers with measured entities in this year's report.",
      value: ytdKpis.ytd_advertisers_live, icon: <TeamOutlined />, color: COLORS.success,
    },
    {
      title: "YTD Partners",
      hint: "Distinct partners that own those advertisers.",
      value: ytdKpis.ytd_partners_live, icon: <TeamOutlined />, color: COLORS.primary,
    },
  ] : [];
  const allCards = [...periodCards, ...ytdCards].filter((c) => !c.hidden);
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      {allCards.map((c) => (
        <Col key={c.title} xs={12} sm={8} md={6} lg={4} xl={3}>
          <Card bordered={false} style={{ borderTop: `3px solid ${c.color}` }}>
            <Statistic
              title={hint(c.title, c.hint)}
              value={c.value}
              prefix={<span style={{ color: c.color, marginRight: 4 }}>{c.icon}</span>}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function ApplicationTable({ data }) {
  const [pageSize, setPageSize] = useState(10);

  const makeFilters = (field) =>
    [...new Set(data.map((d) => d[field]).filter(Boolean))].sort().map((v) => ({ text: v, value: v }));

  const columns = [
    {
      title: "Solution ID",
      dataIndex: "application_id",
      key: "application_id",
      sorter: (a, b) => a.application_id.localeCompare(b.application_id),
      filters: makeFilters("application_id").map((f) => ({ text: upId(f.text), value: f.value })),
      onFilter: (v, r) => r.application_id === v,
      filterSearch: true,
      render: (v) => <span style={{ fontWeight: 500 }}>{upId(v)}</span>,
      fixed: "left",
      width: 120,
    },
    {
      title: "Solution Type",
      dataIndex: "solution_type",
      key: "solution_type",
      filters: makeFilters("solution_type"),
      onFilter: (v, r) => r.solution_type === v,
      render: (v) => <Tag color={v === "Solution Accelerator" ? "orange" : "blue"}>{v}</Tag>,
      width: 160,
    },
    {
      title: "Epic",
      dataIndex: "jira_epic_name",
      key: "jira_epic_name",
      ellipsis: true,
      width: 200,
    },
    {
      title: "Region",
      dataIndex: "jira_region",
      key: "jira_region",
      filters: makeFilters("jira_region"),
      onFilter: (v, r) => r.jira_region === v,
      width: 100,
    },
    {
      title: "Office",
      dataIndex: "jira_ttd_office",
      key: "jira_ttd_office",
      filters: makeFilters("jira_ttd_office"),
      onFilter: (v, r) => r.jira_ttd_office === v,
      width: 130,
    },
    {
      title: "Impressions",
      dataIndex: "impressions",
      key: "impressions",
      sorter: (a, b) => a.impressions - b.impressions,
      defaultSortOrder: "descend",
      render: (v) => <Tooltip title={v?.toLocaleString()}><span>{fmtNum(v)}</span></Tooltip>,
      align: "right",
      width: 110,
    },
    {
      title: "Spend (USD)",
      dataIndex: "partner_cost_usd",
      key: "partner_cost_usd",
      sorter: (a, b) => a.partner_cost_usd - b.partner_cost_usd,
      render: (v) => (
        <Tooltip title={`$${v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}>
          <span>{fmtUsd(v)}</span>
        </Tooltip>
      ),
      align: "right",
      width: 120,
    },
  ];

  return (
    <Card title="Solution Performance Detail" bordered={false}>
      <Table
        dataSource={data.map((d, i) => ({ ...d, key: i }))}
        columns={columns}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
        size="small"
        bordered
        scroll={{ x: true }}
      />
    </Card>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

const EMPTY_FILTERS = { application: [], solution_type: [], consultant: [], region: [], country: [], office: [], jbp: [], accelerator: false, holdco: false };

function Dashboard() {
  const auth = useAuth();
  const token = auth?.user?.id_token;

  const [dashData, setDashData] = useState(null);
  const [solutionTypeMap, setSolutionTypeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [jobStatus, setJobStatus] = useState(null);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [availableDates, setAvailableDates] = useState(null);
  const [metaFilters, setMetaFilters] = useState(EMPTY_FILTERS);
  const pollRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
  };

  const startPolling = (job_id, onComplete) => {
    stopPolling(); // clear any previous poll before starting
    pollRef.current = setInterval(async () => {
      try {
        const job = await GetDashboardJob(token, job_id);
        setJobStatus(job.status);
        if (job.status === "complete") {
          stopPolling();
          onComplete(job.result);
          setLoading(false);
        } else if (job.status === "failed") {
          stopPolling();
          setError(job.error || "Job failed — please try again.");
          setLoading(false);
        }
      } catch (e) {
        stopPolling();
        setError(e.message);
        setLoading(false);
      }
    }, POLL_INTERVAL_MS);

    // Safety timeout — stop polling if job never completes
    pollTimeoutRef.current = setTimeout(() => {
      if (pollRef.current) {
        stopPolling();
        setError("Job is taking too long. Please try again.");
        setLoading(false);
      }
    }, POLL_TIMEOUT_MS);
  };

  useEffect(() => {
    const monthStart = dayjs().startOf("month");
    const monthEnd = dayjs().endOf("month");
    setDateRange([monthStart, monthEnd]);

    async function fetchDefault() {
      try {
        const [dates, mappings] = await Promise.all([
          GetDashboardAvailableDates(token),
          GetSolutionMappings(token).catch(() => []),
        ]);
        setAvailableDates(dates);
        const map = {};
        for (const { application_id, solution_type } of mappings) {
          map[application_id.toLowerCase()] = solution_type;
        }
        setSolutionTypeMap(map);

        const { job_id } = await CreateDashboardJob(token, monthStart.format("YYYY-MM-DD"), monthEnd.format("YYYY-MM-DD"));
        setJobStatus("pending");
        startPolling(job_id, setDashData);
      } catch (e) {
        if (e instanceof AccessError) setAccessDenied(e.message);
        else setError(e.message);
        setLoading(false);
      }
    }
    fetchDefault();
    return () => stopPolling();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoad = async () => {
    if (!dateRange) return;
    stopPolling();
    setError(null);
    setDashData(null);
    setMetaFilters(EMPTY_FILTERS);
    setLoading(true);

    try {
      const { job_id } = await CreateDashboardJob(token, dateRange[0].format("YYYY-MM-DD"), dateRange[1].format("YYYY-MM-DD"));
      setJobStatus("pending");
      startPolling(job_id, setDashData);
    } catch (e) {
      if (e instanceof AccessError) setAccessDenied(e.message);
      else setError(e.message);
      setLoading(false);
    }
  };

  // ─── derived data ─────────────────────────────────────────────────────────────

  const allApps = useMemo(() => dashData?.by_application ?? [], [dashData]);

  const filteredApps = useMemo(() => allApps.filter((app) => {
    if (metaFilters.application.length && !metaFilters.application.includes(app.application_id)) return false;
    if (metaFilters.solution_type.length && !metaFilters.solution_type.includes(app.solution_type)) return false;
    if (metaFilters.consultant.length && !metaFilters.consultant.includes(app.jira_solution_consultant)) return false;
    if (metaFilters.region.length && !metaFilters.region.includes(app.jira_region)) return false;
    if (metaFilters.country.length && !metaFilters.country.includes(app.jira_country)) return false;
    if (metaFilters.office.length && !metaFilters.office.includes(app.jira_ttd_office)) return false;
    if (metaFilters.jbp.length && !metaFilters.jbp.includes(app.jira_joint_business_plan_partner)) return false;
    if (metaFilters.accelerator && solutionTypeMap[app.application_id?.toLowerCase()] !== "Solution Accelerator") return false;
    if (metaFilters.holdco && !app.is_holdco) return false;
    return true;
  }), [allApps, metaFilters, solutionTypeMap]);

  const filteredSummary = useMemo(() => buildSummary(filteredApps), [filteredApps]);
  const filteredSolutionType = useMemo(() => aggregateSolutionType(filteredApps), [filteredApps]);
  const hasActiveFilters = Object.values(metaFilters).some((v) => Array.isArray(v) ? v.length > 0 : v);
  const singleApp = filteredApps.length === 1;
  const isFiltered = hasActiveFilters;
  const topN = isFiltered ? "" : "Top 10 ";

  // ─── render ───────────────────────────────────────────────────────────────────

  if (accessDenied) return <NoAccess message={accessDenied} />;

  const disabledDate = (d) => {
    if (!d) return false;
    if (availableDates) return d < dayjs(availableDates.date_from).startOf("day") || d > dayjs(availableDates.date_to).endOf("day");
    return d > dayjs().endOf("day");
  };

  const topBar = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
      <Title level={3} style={{ margin: 0 }}>Custom Solutions - Solution Level Commercial Impact Measurement Report</Title>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {dateRange && (
          <Text type="secondary">
            <CalendarOutlined style={{ marginRight: 4 }} />
            {dateRange[0].format("YYYY-MM-DD")} → {dateRange[1].format("YYYY-MM-DD")}
          </Text>
        )}
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          disabledDate={disabledDate}
          format="YYYY-MM-DD"
          allowClear
          placeholder={["Start date", "End date"]}
        />
        <Button
          type="primary"
          icon={loading ? <LoadingOutlined /> : <ReloadOutlined />}
          onClick={handleLoad}
          disabled={loading}
        >
          {loading ? "Loading..." : "Load"}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    const statusMsg = { pending: "Job queued — waiting to start...", running: "Processing your date range..." }[jobStatus] || "Loading report data...";
    return (
      <div style={{ padding: 24 }}>
        {topBar}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 80, gap: 16 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
          <Text type="secondary">{statusMsg}</Text>

        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        {topBar}
        {error.toLowerCase().includes("no report")
          ? <Empty description={error} style={{ marginTop: 64 }} />
          : <Alert message="Failed to load dashboard" description={error} type="error" showIcon />}
      </div>
    );
  }

  if (!dashData) return null;

  const filterBar = (
    <Card bordered={false} style={{ marginBottom: 16 }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={6}>
          <Select mode="multiple" allowClear placeholder="Solution Type" style={{ width: "100%" }}
            value={metaFilters.solution_type}
            onChange={(v) => setMetaFilters((f) => ({ ...f, solution_type: v ?? [] }))}
            options={uniqueOptions(allApps, "solution_type")}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select mode="multiple" allowClear showSearch placeholder="Region" style={{ width: "100%" }}
            value={metaFilters.region}
            onChange={(v) => setMetaFilters((f) => ({ ...f, region: v ?? [] }))}
            options={uniqueOptions(allApps, "jira_region")}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select mode="multiple" allowClear showSearch placeholder="Office" style={{ width: "100%" }}
            value={metaFilters.office}
            onChange={(v) => setMetaFilters((f) => ({ ...f, office: v ?? [] }))}
            options={uniqueOptions(allApps, "jira_ttd_office")}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select mode="multiple" allowClear showSearch placeholder="JBP Partner" style={{ width: "100%" }}
            value={metaFilters.jbp}
            onChange={(v) => setMetaFilters((f) => ({ ...f, jbp: v ?? [] }))}
            options={uniqueOptions(allApps, "jira_joint_business_plan_partner")}
          />
        </Col>
        <Col xs={24} sm={12} md={6} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Switch
            checked={metaFilters.accelerator}
            onChange={(v) => setMetaFilters((f) => ({ ...f, accelerator: v }))}
          />
          <Text>Accelerator Only</Text>
        </Col>
        <Col xs={24} sm={12} md={6} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Switch
            checked={metaFilters.holdco}
            onChange={(v) => setMetaFilters((f) => ({ ...f, holdco: v }))}
          />
          <Text>Hold Co. Project?</Text>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Select
            mode="multiple" allowClear showSearch placeholder="Application"
            style={{ width: "100%" }}
            value={metaFilters.application}
            onChange={(v) => setMetaFilters((f) => ({ ...f, application: v ?? [] }))}
            optionFilterProp="label"
            options={[...new Map(allApps.map((a) => [a.application_id, a])).values()]
              .sort((a, b) => a.application_id.localeCompare(b.application_id))
              .map((a) => ({
                value: a.application_id,
                label: `${upId(a.application_id)}${a.jira_epic_name ? ` — ${a.jira_epic_name}` : ""}`,
              }))}
            optionRender={(opt) => (
              <div>
                <div style={{ fontWeight: 500 }}>{upId(opt.data.value)}</div>
                {opt.data.label.includes(" — ") && (
                  <div style={{ fontSize: 11, color: "#888" }}>{opt.data.label.split(" — ").slice(1).join(" — ")}</div>
                )}
              </div>
            )}
          />
        </Col>
        {hasActiveFilters && (
          <Col xs={24} sm={12} md={6} style={{ display: "flex", alignItems: "center" }}>
            <Button icon={<ClearOutlined />} onClick={() => setMetaFilters(EMPTY_FILTERS)} block>Clear Filters</Button>
          </Col>
        )}
      </Row>
    </Card>
  );

  // spend/impression data with pre-computed display keys
  const appsWithSpendK = filteredApps.map((d) => ({ ...d, spend_k: +(d.partner_cost_usd / 1000).toFixed(2) }));

  return (
    <div style={{ padding: 24 }}>
      {topBar}
      {filterBar}
      <KpiCards summary={filteredSummary} ytdKpis={dashData?.ytd_kpis} />

      {!singleApp && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={16}>
            <AppBarChart
              title={`${topN}Solutions by Spend (USD)`}
              data={appsWithSpendK}
              dataKey="spend_k"
              tickFormatter={(v) => `$${v}K`}
              tooltipFormatter={(v) => [`$${v}K`, "Spend"]}
            />
          </Col>
          <Col xs={24} lg={8}>
            <SolutionTypePie data={filteredSolutionType} dataKey="partner_cost_usd" title="Spend by Solution Type" />
          </Col>
        </Row>
      )}

      <ApplicationTable data={filteredApps} />
      <FaqSection />
    </div>
  );
}

export default Dashboard;
